import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $applyNodeReplacement, $isTextNode, type LexicalNode, type NodeKey, type SerializedLexicalNode, type Spread } from "lexical";
import { Calendar, Clock, Target, TrendingUp } from "lucide-react";
import { JSX, useCallback, useEffect, useRef, useState } from "react";
import { NodeDeleteButton } from "@/components/Editor/NodeDeleteButton";
import { useDebouncedUpdate, useImmediateUpdate } from "@/components/Editor/useDebouncedUpdate";
import { TickerAutoComplete } from "@/components/TickerAutoComplete";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  CONTRACT_STATUS,
  CONTRACT_TEMPLATE,
  type ContractStatus,
  type ContractTemplate,
  TRIGGER_OPERATOR,
  TRIGGER_TYPE,
  type TriggerOperator,
  type TriggerType,
} from "@/constants/lexical";
import { cn } from "@/lib/utils";
import { useTranslate } from "@/utils/i18n";
import { BaseAppNode, registerBaseAppNodeType } from "./BaseAppNode";
import { WindowDecorator } from "./WindowNode";

// Register this node type with BaseAppNode
registerBaseAppNodeType("self-contract");

// =============================================================================
// Type Definitions
// =============================================================================

export type SerializedSelfContractNode = Spread<
  {
    type: "self-contract";
    version: 1;
    ticker: string;
    template: ContractTemplate;
    expiration: string; // ISO 8601 date (YYYY-MM-DD)
    idea: string;
    triggerType: TriggerType;
    triggerPriceLevel?: number;
    triggerOperator?: TriggerOperator;
    triggerNotes: string;
    action: string;
    status: ContractStatus;
    createdAt?: string; // ISO 8601 timestamp
    updatedAt?: string; // ISO 8601 timestamp
  },
  SerializedLexicalNode
>;

/**
 * SelfContractData - Self-Contract node data (without Lexical metadata)
 */
export type SelfContractData = Omit<SerializedSelfContractNode, keyof SerializedLexicalNode>;

// =============================================================================
// SelfContractNode
// =============================================================================

/**
 * SelfContractNode - Self-Contract 节点
 *
 * Self-Contract 节点包含：
 * - Ticker: 股票代码
 * - Template: 模板类型（价值导向或波段导向）
 * - Expiration: 到期日期
 * - Idea: 投资观点/策略
 * - Trigger: 触发条件
 * - Action: 触发后采取的行动
 * - Status: 合约状态
 *
 * Per architecture: Node holds value fields directly. Trigger is flattened from nested structure.
 */
export class SelfContractNode extends BaseAppNode {
  __ticker: string;
  __template: ContractTemplate;
  __expiration: string;
  __idea: string;
  __triggerType: TriggerType;
  __triggerPriceLevel?: number;
  __triggerOperator?: TriggerOperator;
  __triggerNotes: string;
  __action: string;
  __status: ContractStatus;
  __createdAt?: string;
  __updatedAt?: string;

  static getType(): string {
    return "self-contract";
  }

  static clone(node: SelfContractNode): SelfContractNode {
    return new SelfContractNode(
      node.__ticker,
      node.__template,
      node.__expiration,
      node.__idea,
      node.__triggerType,
      node.__triggerPriceLevel,
      node.__triggerOperator,
      node.__triggerNotes,
      node.__action,
      node.__status,
      node.__createdAt,
      node.__updatedAt,
      node.__key,
    );
  }

  constructor(
    ticker: string,
    template: ContractTemplate,
    expiration: string,
    idea: string,
    triggerType: TriggerType,
    triggerPriceLevel?: number,
    triggerOperator?: TriggerOperator,
    triggerNotes?: string,
    action?: string,
    status?: ContractStatus,
    createdAt?: string,
    updatedAt?: string,
    key?: NodeKey,
  ) {
    super(false, key); // false = block-level node
    this.__ticker = ticker;
    this.__template = template;
    this.__expiration = expiration;
    this.__idea = idea;
    this.__triggerType = triggerType;
    this.__triggerPriceLevel = triggerPriceLevel;
    this.__triggerOperator = triggerOperator;
    this.__triggerNotes = triggerNotes ?? "";
    this.__action = action ?? "";
    this.__status = status ?? CONTRACT_STATUS.ACTIVE;
    this.__createdAt = createdAt;
    this.__updatedAt = updatedAt;
  }

  // ========== DOM 操作 ==========

  createDOM(): HTMLElement {
    const div = document.createElement("div");
    div.className = "self-contract-node-container";
    div.setAttribute("data-lexical-decorator", "true");
    return div;
  }

  updateDOM(): boolean {
    return false;
  }

  decorate(): JSX.Element {
    return <SelfContractDecorator node={this} />;
  }

  // ========== 序列化 ==========

  static importJSON(serializedNode: SerializedSelfContractNode): SelfContractNode {
    return $createSelfContractNode({
      ticker: serializedNode.ticker,
      template: serializedNode.template,
      expiration: serializedNode.expiration,
      idea: serializedNode.idea,
      triggerType: serializedNode.triggerType,
      triggerPriceLevel: serializedNode.triggerPriceLevel,
      triggerOperator: serializedNode.triggerOperator,
      triggerNotes: serializedNode.triggerNotes,
      action: serializedNode.action,
      status: serializedNode.status,
      createdAt: serializedNode.createdAt ?? "",
      updatedAt: serializedNode.updatedAt ?? "",
    });
  }

  exportJSON(): SerializedSelfContractNode {
    return {
      type: "self-contract",
      version: 1,
      ticker: this.__ticker,
      template: this.__template,
      expiration: this.__expiration,
      idea: this.__idea,
      triggerType: this.__triggerType,
      triggerPriceLevel: this.__triggerPriceLevel,
      triggerOperator: this.__triggerOperator,
      triggerNotes: this.__triggerNotes,
      action: this.__action,
      status: this.__status,
      createdAt: this.__createdAt,
      updatedAt: this.__updatedAt,
    };
  }

  // ========== Getter/Setter ==========

  getTicker(): string {
    return this.__ticker;
  }

  setTicker(ticker: string): void {
    const writable = this.getWritable();
    writable.__ticker = ticker;
    writable.__updatedAt = new Date().toISOString();
  }

  getTemplate(): ContractTemplate {
    return this.__template;
  }

  setTemplate(template: ContractTemplate): void {
    const writable = this.getWritable();
    writable.__template = template;
    writable.__updatedAt = new Date().toISOString();
  }

  getExpiration(): string {
    return this.__expiration;
  }

  setExpiration(expiration: string): void {
    const writable = this.getWritable();
    writable.__expiration = expiration;
    writable.__updatedAt = new Date().toISOString();
  }

  getIdea(): string {
    return this.__idea;
  }

  setIdea(idea: string): void {
    const writable = this.getWritable();
    writable.__idea = idea;
    writable.__updatedAt = new Date().toISOString();
  }

  getTriggerType(): TriggerType {
    return this.__triggerType;
  }

  setTriggerType(triggerType: TriggerType): void {
    const writable = this.getWritable();
    writable.__triggerType = triggerType;
    writable.__updatedAt = new Date().toISOString();
  }

  getTriggerPriceLevel(): number | null {
    return this.__triggerPriceLevel ?? null;
  }

  setTriggerPriceLevel(priceLevel: number | null): void {
    const writable = this.getWritable();
    writable.__triggerPriceLevel = priceLevel ?? undefined;
    writable.__updatedAt = new Date().toISOString();
  }

  getTriggerOperator(): TriggerOperator | null {
    return this.__triggerOperator ?? null;
  }

  setTriggerOperator(operator: TriggerOperator | null): void {
    const writable = this.getWritable();
    writable.__triggerOperator = operator ?? undefined;
    writable.__updatedAt = new Date().toISOString();
  }

  getTriggerNotes(): string {
    return this.__triggerNotes;
  }

  setTriggerNotes(notes: string): void {
    const writable = this.getWritable();
    writable.__triggerNotes = notes;
    writable.__updatedAt = new Date().toISOString();
  }

  getAction(): string {
    return this.__action;
  }

  setAction(action: string): void {
    const writable = this.getWritable();
    writable.__action = action;
    writable.__updatedAt = new Date().toISOString();
  }

  getStatus(): ContractStatus {
    return this.__status;
  }

  setStatus(status: ContractStatus): void {
    const writable = this.getWritable();
    writable.__status = status;
    writable.__updatedAt = new Date().toISOString();
  }

  getCreatedAt(): string {
    return this.__createdAt || "";
  }

  getUpdatedAt(): string {
    return this.__updatedAt || "";
  }

  // ========== 其他 ==========

  getTextContent(): string {
    const templateLabel = this.__template === CONTRACT_TEMPLATE.VALUE_ORIENTED ? "[价值导向]" : "[波段导向]";
    const statusLabel = this.getStatusLabel();
    const triggerInfo = this.getTriggerSummary();
    return `自定约 $${this.__ticker} ${templateLabel} ${statusLabel} - ${triggerInfo} | 策略: ${this.__idea}`;
  }

  isEqualTo(other: LexicalNode): boolean {
    if (!(other instanceof SelfContractNode)) {
      return false;
    }
    return (
      this.__ticker === other.__ticker &&
      this.__template === other.__template &&
      this.__expiration === other.__expiration &&
      this.__status === other.__status
    );
  }

  // ========== 辅助方法 ==========

  getStatusLabel(): string {
    switch (this.__status) {
      case CONTRACT_STATUS.ACTIVE:
        return "进行中";
      case CONTRACT_STATUS.TRIGGERED:
        return "已触发";
      case CONTRACT_STATUS.CANCELLED:
        return "已取消";
      case CONTRACT_STATUS.EXPIRED:
        return "已过期";
      default:
        return "未知";
    }
  }

  getTriggerSummary(): string {
    switch (this.__triggerType) {
      case TRIGGER_TYPE.PRICE: {
        const op = this.__triggerOperator || "==";
        const price = this.__triggerPriceLevel ?? 0;
        return `价格 ${op} ${price.toFixed(2)}`;
      }
      case TRIGGER_TYPE.EXPIRATION:
        return `到期: ${this.__expiration}`;
      case TRIGGER_TYPE.TIME:
        return this.__triggerNotes;
      case TRIGGER_TYPE.CUSTOM:
        return this.__triggerNotes;
      default:
        return "未设置";
    }
  }
}

// =============================================================================
// Decorator Component
// =============================================================================

/**
 * Self-Contract 节点编辑器组件
 *
 * 修复所有问题:
 * 1. Lexical error #195 - 所有节点更新都在 editor.update() 中执行
 * 2. 增加 TickerAutoComplete 宽度从 w-24 到 w-36
 */
function SelfContractDecorator({ node }: { node: SelfContractNode }) {
  const t = useTranslate();
  const [editor] = useLexicalComposerContext();

  // Local state for all form fields (status is always "active", not editable)
  const [ticker, setTicker] = useState(node.getTicker());
  const [template, setTemplate] = useState(node.getTemplate());
  const [expiration, setExpiration] = useState(node.getExpiration());
  const [idea, setIdea] = useState(node.getIdea());
  const [triggerType, setTriggerType] = useState(node.getTriggerType());
  const [triggerPriceLevel, setTriggerPriceLevel] = useState(node.getTriggerPriceLevel());
  const [triggerOperator, setTriggerOperator] = useState(node.getTriggerOperator());
  const [triggerNotes, setTriggerNotes] = useState(node.getTriggerNotes());
  const [action, setAction] = useState(node.getAction());

  // Store previous values to detect actual changes
  const prevValuesRef = useRef({
    ticker,
    template,
    expiration,
    idea,
    triggerType,
    triggerPriceLevel,
    triggerOperator,
    triggerNotes,
    action,
  });

  // Sync local state with node when node is updated externally
  useEffect(() => {
    setTicker(node.getTicker());
    setTemplate(node.getTemplate());
    setExpiration(node.getExpiration());
    setIdea(node.getIdea());
    setTriggerType(node.getTriggerType());
    setTriggerPriceLevel(node.getTriggerPriceLevel());
    setTriggerOperator(node.getTriggerOperator());
    setTriggerNotes(node.getTriggerNotes());
    setAction(node.getAction());
    prevValuesRef.current = {
      ticker: node.getTicker(),
      template: node.getTemplate(),
      expiration: node.getExpiration(),
      idea: node.getIdea(),
      triggerType: node.getTriggerType(),
      triggerPriceLevel: node.getTriggerPriceLevel(),
      triggerOperator: node.getTriggerOperator(),
      triggerNotes: node.getTriggerNotes(),
      action: node.getAction(),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-delete callback: Clean up orphaned "/" text from previous paragraph
  const handleOnBeforeDelete = useCallback(() => {
    editor.update(() => {
      const prevSibling = node.getPreviousSibling();
      if ($isTextNode(prevSibling)) {
        const writableNode = prevSibling.getWritable();
        const textContent = writableNode.getTextContent();
        if (textContent === "/" || textContent.endsWith("/")) {
          writableNode.setTextContent(textContent.replace(/\/+$/, ""));
        }
      }
    });
  }, [editor, node]);

  // Debounced update function for ticker - runs in editor.update() context
  const updateTicker = useDebouncedUpdate(
    (value: string) => {
      if (value !== prevValuesRef.current.ticker) {
        node.setTicker(value);
        prevValuesRef.current.ticker = value;
      }
    },
    () => editor,
    300,
  );

  // Immediate update function for required fields (validation needs latest value)
  const updateExpiration = useImmediateUpdate(
    (value: string) => {
      if (value !== prevValuesRef.current.expiration) {
        node.setExpiration(value);
        prevValuesRef.current.expiration = value;
      }
    },
    () => editor,
  );

  const updateIdea = useImmediateUpdate(
    (value: string) => {
      if (value !== prevValuesRef.current.idea) {
        node.setIdea(value);
        prevValuesRef.current.idea = value;
      }
    },
    () => editor,
  );

  // Debounced update function for trigger notes
  const updateTriggerNotes = useDebouncedUpdate(
    (value: string) => {
      if (value !== prevValuesRef.current.triggerNotes) {
        node.setTriggerNotes(value);
        prevValuesRef.current.triggerNotes = value;
      }
    },
    () => editor,
    300,
  );

  // Debounced update function for price level
  const updateTriggerPriceLevel = useDebouncedUpdate(
    (value: number | null) => {
      if (value !== prevValuesRef.current.triggerPriceLevel) {
        node.setTriggerPriceLevel(value);
        prevValuesRef.current.triggerPriceLevel = value;
      }
    },
    () => editor,
    300,
  );

  // Debounced update function for trigger operator
  const updateTriggerOperator = useDebouncedUpdate(
    (value: TriggerOperator | null) => {
      if (value !== prevValuesRef.current.triggerOperator) {
        node.setTriggerOperator(value);
        prevValuesRef.current.triggerOperator = value;
      }
    },
    () => editor,
    300,
  );

  // Immediate update function for action (required field)
  const updateAction = useImmediateUpdate(
    (value: string) => {
      if (value !== prevValuesRef.current.action) {
        node.setAction(value);
        prevValuesRef.current.action = value;
      }
    },
    () => editor,
  );

  // Immediate update for template (dropdown) - also in editor.update() context
  const handleTemplateChange = useCallback(
    (newTemplate: ContractTemplate) => {
      setTemplate(newTemplate);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          editor.update(() => {
            if (newTemplate !== prevValuesRef.current.template) {
              node.setTemplate(newTemplate);
              prevValuesRef.current.template = newTemplate;
            }
          });
        });
      });
    },
    [editor, node],
  );

  // Immediate update for trigger type (dropdown)
  const handleTriggerTypeChange = useCallback(
    (newType: TriggerType) => {
      setTriggerType(newType);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          editor.update(() => {
            if (newType !== prevValuesRef.current.triggerType) {
              node.setTriggerType(newType);
              prevValuesRef.current.triggerType = newType;
            }
          });
        });
      });
    },
    [editor, node],
  );

  // Handle ticker input change
  const handleTickerChange = (value: string) => {
    const uppercaseValue = value.toUpperCase();
    setTicker(uppercaseValue);
    updateTicker(uppercaseValue);
  };

  // Handle ticker auto-complete selection
  const handleTickerSelect = (symbol: string) => {
    handleTickerChange(symbol);
  };

  return (
    <WindowDecorator
      node={node}
      className="my-4 rounded-lg border bg-card shadow-sm relative z-0 w-full group"
      onBeforeDelete={handleOnBeforeDelete}
    >
      {(handleDelete) => (
        <>
          {/* Header: Ticker + Template + Status + Delete */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-primary" />
                <TickerAutoComplete
                  value={ticker}
                  onChange={handleTickerChange}
                  onSelect={handleTickerSelect}
                  placeholder=""
                  className="w-36"
                  inputClassName={cn("h-8 font-bold text-center uppercase px-2", !ticker && "border-destructive focus:border-destructive")}
                />
                {!ticker && <span className="text-destructive">*</span>}
              </div>
              <div className="h-4 w-px bg-border" />
              <Select value={template} onValueChange={handleTemplateChange}>
                <SelectTrigger className="h-8 w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={CONTRACT_TEMPLATE.VALUE_ORIENTED}>
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-3 h-3" />
                      {t("editor.selfContractDetail.valueOriented" as any)}
                    </div>
                  </SelectItem>
                  <SelectItem value={CONTRACT_TEMPLATE.TRADING_ORIENTED}>
                    <div className="flex items-center gap-2">
                      <Clock className="w-3 h-3" />
                      {t("editor.selfContractDetail.tradingOriented" as any)}
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Badge
                variant="default"
                className="h-8 px-3 flex items-center gap-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
              >
                {t("editor.selfContractDetail.inProgress" as any)}
              </Badge>
              <NodeDeleteButton onClick={handleDelete} title={t("editor.selfContractDetail.delete" as any)} variant="hover-show" />
            </div>
          </div>

          {/* Body */}
          <div className="p-4 space-y-4">
            {/* Expiration Date */}
            <div className="flex items-center gap-2">
              <Label htmlFor="expiration" className="text-sm font-medium w-24 flex items-center gap-1 flex-shrink-0">
                <Calendar className="w-3 h-3" />
                {t("editor.selfContractDetail.expirationDate" as any)}
                <span className="text-destructive">*</span>
              </Label>
              <Input
                id="expiration"
                type="date"
                value={expiration}
                onChange={(e) => {
                  setExpiration(e.target.value);
                  updateExpiration(e.target.value);
                }}
                className="border-0"
              />
            </div>

            {/* Idea */}
            <div>
              <Label htmlFor="idea" className="text-sm font-medium flex items-center gap-1 mb-2">
                {t("editor.selfContractDetail.idea" as any)}
                <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="idea"
                value={idea}
                onChange={(e) => {
                  setIdea(e.target.value);
                  updateIdea(e.target.value);
                }}
                placeholder={t("editor.selfContractDetail.idea-placeholder" as any)}
                className={cn("min-h-[60px] resize-none", !idea && "border-destructive focus:border-destructive")}
                rows={2}
              />
              {!idea && <p className="text-xs text-destructive mt-1">{t("editor.selfContractDetail.idea-required" as any)}</p>}
            </div>

            {/* Trigger */}
            <div>
              <Label className="text-sm font-medium flex items-center gap-1 mb-2">
                {t("editor.selfContractDetail.triggerCondition" as any)}
              </Label>
              <div className="space-y-2">
                <Select value={triggerType} onValueChange={handleTriggerTypeChange}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={TRIGGER_TYPE.PRICE}>{t("editor.selfContractDetail.priceTrigger" as any)}</SelectItem>
                    <SelectItem value={TRIGGER_TYPE.EXPIRATION}>{t("editor.selfContractDetail.expirationTrigger" as any)}</SelectItem>
                    <SelectItem value={TRIGGER_TYPE.TIME}>{t("editor.selfContractDetail.timeTrigger" as any)}</SelectItem>
                    <SelectItem value={TRIGGER_TYPE.CUSTOM}>{t("editor.selfContractDetail.custom" as any)}</SelectItem>
                  </SelectContent>
                </Select>

                {/* Price-specific fields */}
                {triggerType === TRIGGER_TYPE.PRICE && (
                  <>
                    <div className="flex gap-2">
                      <Select
                        value={triggerOperator || TRIGGER_OPERATOR.GREATER_THAN_OR_EQUAL}
                        onValueChange={(val) => {
                          const operator = val as TriggerOperator;
                          setTriggerOperator(operator);
                          updateTriggerOperator(operator);
                        }}
                      >
                        <SelectTrigger className={cn("h-9 w-24", !triggerOperator && "border-destructive")}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={TRIGGER_OPERATOR.GREATER_THAN_OR_EQUAL}>≥</SelectItem>
                          <SelectItem value={TRIGGER_OPERATOR.LESS_THAN_OR_EQUAL}>≤</SelectItem>
                          <SelectItem value={TRIGGER_OPERATOR.GREATER_THAN}>&gt;</SelectItem>
                          <SelectItem value={TRIGGER_OPERATOR.LESS_THAN}>&lt;</SelectItem>
                          <SelectItem value={TRIGGER_OPERATOR.EQUAL}>=</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        step="0.01"
                        value={triggerPriceLevel ?? ""}
                        onChange={(e) => {
                          const val = e.target.value ? parseFloat(e.target.value) : null;
                          setTriggerPriceLevel(val);
                          updateTriggerPriceLevel(val);
                        }}
                        placeholder={t("editor.selfContractDetail.price" as any)}
                        className={cn("flex-1 h-9", (!triggerPriceLevel || triggerPriceLevel <= 0) && "border-destructive")}
                      />
                      {(!triggerPriceLevel || triggerPriceLevel <= 0) && (
                        <p className="text-xs text-destructive">{t("editor.selfContractDetail.price-error" as any)}</p>
                      )}
                    </div>
                    <Textarea
                      value={triggerNotes}
                      onChange={(e) => {
                        setTriggerNotes(e.target.value);
                        updateTriggerNotes(e.target.value);
                      }}
                      placeholder={
                        triggerType === TRIGGER_TYPE.PRICE
                          ? t("editor.selfContractDetail.price-notes-placeholder" as any)
                          : triggerType === TRIGGER_TYPE.EXPIRATION
                            ? t("editor.selfContractDetail.expiration-notes-placeholder" as any)
                            : t("editor.selfContractDetail.time-notes-placeholder" as any)
                      }
                      className="min-h-[40px] resize-none"
                      rows={1}
                    />
                  </>
                )}
              </div>
            </div>

            {/* Action */}
            <div>
              <Label htmlFor="action" className="text-sm font-medium mb-2 block">
                {t("editor.selfContractDetail.action" as any)}
                <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="action"
                value={action}
                onChange={(e) => {
                  setAction(e.target.value);
                  updateAction(e.target.value);
                }}
                placeholder={t("editor.selfContractDetail.action-placeholder" as any)}
                className={`min-h-[60px] resize-none ${!action && "border-destructive focus:border-destructive"}`}
                rows={2}
              />
              {!action && <span className="text-destructive text-xs">{t("editor.selfContractDetail.action-required" as any)}</span>}
            </div>
          </div>
        </>
      )}
    </WindowDecorator>
  );
}

// =============================================================================
// Factory Functions
// =============================================================================

export function $createSelfContractNode(data?: Partial<SelfContractData>): SelfContractNode {
  const node = new SelfContractNode(
    data?.ticker || "",
    data?.template || CONTRACT_TEMPLATE.VALUE_ORIENTED,
    data?.expiration || "",
    data?.idea || "",
    data?.triggerType || TRIGGER_TYPE.PRICE,
    data?.triggerPriceLevel,
    data?.triggerOperator || TRIGGER_OPERATOR.GREATER_THAN_OR_EQUAL,
    data?.triggerNotes || "",
    data?.action || "",
    data?.status || CONTRACT_STATUS.ACTIVE,
    data?.createdAt,
    data?.updatedAt,
    undefined, // key (last parameter)
  );
  return $applyNodeReplacement(node);
}

export function $createValueOrientedContract(initialData?: {
  ticker?: string;
  expiration?: string;
  idea?: string;
  action?: string;
}): SelfContractNode {
  return $createSelfContractNode({
    ticker: initialData?.ticker,
    template: CONTRACT_TEMPLATE.VALUE_ORIENTED,
    expiration: initialData?.expiration || "",
    idea: initialData?.idea || "",
    triggerType: TRIGGER_TYPE.PRICE,
    triggerOperator: TRIGGER_OPERATOR.GREATER_THAN_OR_EQUAL,
    triggerNotes: "",
    action: initialData?.action || "",
  });
}

export function $createTradingOrientedContract(initialData?: {
  ticker?: string;
  expiration?: string;
  idea?: string;
  action?: string;
}): SelfContractNode {
  return $createSelfContractNode({
    ticker: initialData?.ticker,
    template: CONTRACT_TEMPLATE.TRADING_ORIENTED,
    expiration: initialData?.expiration || "",
    idea: initialData?.idea || "",
    triggerType: TRIGGER_TYPE.PRICE,
    triggerOperator: TRIGGER_OPERATOR.GREATER_THAN_OR_EQUAL,
    triggerNotes: "",
    action: initialData?.action || "",
  });
}
